/**
 * Canonical persona set — the SINGLE source of truth for who Avium is for. The
 * same six personas appear in marketing (/for/<marketingSlug>), the first-run
 * role prompt, the "Your role" setting, the /today lens fork, and the daily
 * briefing's framing. Keep this in lockstep with server/src/lib/personas.ts and
 * the marketing personaLearnContent.ts (CLAUDE.md: persona vocabulary must match
 * across every surface).
 *
 * Each persona maps to one of two experience LENSES:
 *  - 'team'    — facilitates/improves the day-to-day work, embedded with the
 *                team(s): "what do we act on today" (the /today Team view).
 *  - 'leaders' — accountable for delivery outcomes, consumes cross-team/portfolio
 *                rollups: "where's the risk across everything" (the Leaders view).
 */

export type PersonaLens = 'team' | 'leaders';
export type PersonaKey =
  | 'scrum-master' | 'agile-coach' | 'project-manager'
  | 'engineering-manager' | 'vp-engineering' | 'pmo';

export interface Persona {
  key: PersonaKey;
  label: string;        // UI label
  lens: PersonaLens;    // which /today + briefing experience they get
  scopeHint: string;    // default scope framing ("one team", "the whole org", …)
  blurb: string;        // one-liner for the picker
  todayTagline: string; // persona-specific framing on /today + the briefing
  marketingSlug: string; // /for/<slug> cross-link
}

export const PERSONAS: Persona[] = [
  { key: 'scrum-master',        label: 'Scrum Master',         lens: 'team',    scopeHint: 'one team',            blurb: 'Run the standup; keep this sprint on track.',                  todayTagline: "Today's standup — what to run, who's blocked.",                marketingSlug: 'scrum-masters' },
  { key: 'agile-coach',         label: 'Agile Coach',          lens: 'team',    scopeHint: 'the teams you coach', blurb: 'Improve how several teams flow and hand off work.',             todayTagline: 'How your teams are flowing — hand-offs, drift, bottlenecks.',  marketingSlug: 'agile-coaches' },
  { key: 'project-manager',     label: 'Project Manager',      lens: 'team',    scopeHint: 'your project(s)',     blurb: 'Coordinate delivery; clear blockers across your project(s).',   todayTagline: "Your project today — risks, blockers, what slipped.",          marketingSlug: 'project-managers' },
  { key: 'engineering-manager', label: 'Engineering Manager',  lens: 'leaders', scopeHint: 'your team(s)',        blurb: 'Will my team(s) deliver — and who needs a 1:1 today?',          todayTagline: 'Will your team(s) deliver — and who needs a 1:1 today?',        marketingSlug: 'engineering-managers' },
  { key: 'vp-engineering',      label: 'VP of Engineering',    lens: 'leaders', scopeHint: 'the whole org',       blurb: 'Where is the risk across every team this week?',                todayTagline: "Where's the risk across every team this week?",                marketingSlug: 'vps' },
  { key: 'pmo',                 label: 'PMO',                  lens: 'leaders', scopeHint: 'your portfolio',      blurb: 'Portfolio risk and status across every project.',              todayTagline: 'Portfolio risk and status across your projects.',              marketingSlug: 'pmos' },
];

const BY_KEY = new Map(PERSONAS.map(p => [p.key, p]));

export function getPersona(key: string | null | undefined): Persona | null {
  return key ? BY_KEY.get(key as PersonaKey) ?? null : null;
}
export function isPersonaKey(key: string): key is PersonaKey {
  return BY_KEY.has(key as PersonaKey);
}
export function personaLens(key: string | null | undefined): PersonaLens | null {
  return getPersona(key)?.lens ?? null;
}

// Picker grouping — Team first (most users), then Leaders.
export const PERSONAS_BY_LENS: Record<PersonaLens, Persona[]> = {
  team: PERSONAS.filter(p => p.lens === 'team'),
  leaders: PERSONAS.filter(p => p.lens === 'leaders'),
};
export const LENS_LABEL: Record<PersonaLens, string> = { team: 'On a team', leaders: 'Leading delivery' };
